import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import HumanCheck from '../components/HumanCheck';
import { createApiUrl } from '../utils/apiConfig';
import styles from './Register.module.css';

const RegisterTeacher = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    contactName: '',
    email: '',
    password: '',
    confirmPassword: '',
    schoolCode: '',
    schoolName: '',
    address: '',
    postZipCode: '',
    website: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showAddressFields, setShowAddressFields] = useState(false);
  const [loadingSchool, setLoadingSchool] = useState(false);
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [humanCheckValid, setHumanCheckValid] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const fetchSchoolDetails = async (schoolCode) => {
    if (!schoolCode || schoolCode.length < 3) {
      setSchoolInfo(null);
      setShowAddressFields(false);
      return;
    }

    setLoadingSchool(true);
    try {
      const response = await axios.get(createApiUrl(`/api/schools/code/${schoolCode}`));
      if (response.data.success) {
        const schoolData = response.data.data;
        setSchoolInfo(schoolData);
        setShowAddressFields(true);
        
        // Auto-populate the form fields with school data
        setFormData(prev => ({
          ...prev,
          schoolName: schoolData.schoolName || '',
          address: schoolData.address || '',
          postZipCode: schoolData.postZipCode || '',
          website: schoolData.website || ''
        }));
        
        setError('');
      }
    } catch (err) {
      setSchoolInfo(null);
      setShowAddressFields(false);
      // Don't show error for invalid school codes - just clear the fields
    } finally {
      setLoadingSchool(false);
    }
  };

  const handleSchoolCodeChange = (e) => {
    const schoolCode = e.target.value;
    setFormData({
      ...formData,
      schoolCode: schoolCode
    });
    
    // Show address fields if school code is entered
    setShowAddressFields(schoolCode.length > 0);
    
    // Fetch school details if school code is entered
    if (schoolCode.length > 0) {
      fetchSchoolDetails(schoolCode);
    } else {
      setSchoolInfo(null);
      setShowAddressFields(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    if (!formData.schoolCode) {
      setError('School Code is required');
      setLoading(false);
      return;
    }

    if (!humanCheckValid) {
      setError('Please answer the human verification question correctly.');
      setLoading(false);
      return;
    }

    try {
      const requestData = {
        username: formData.email, // Username equals email address
        contactName: formData.contactName,
        email: formData.email,
        password: formData.password,
        schoolCode: formData.schoolCode,
        schoolName: formData.schoolName,
        address: formData.address,
        postZipCode: formData.postZipCode,
        website: formData.website
      };

      const response = await axios.post(createApiUrl('/api/register/teacher'), requestData);

      if (response.data.success) {
        setSuccess('Teacher registration successful! Redirecting to dashboard...');
        
        // Store token and user data
        localStorage.setItem('token', response.data.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.data.teacher));
        
        // Redirect to teacher dashboard after 2 seconds
        setTimeout(() => {
          navigate('/teacher-dashboard');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const togglePassword = () => setShowPassword((v) => !v);
  const toggleConfirmPassword = () => setShowConfirmPassword((v) => !v);

  return (
    <div className={styles.registerSchoolContainer}>
      <h1 className={styles.registerSchoolHeading}>Register<br />Teacher</h1>
      <div className={styles.registerSchoolCard}>
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
        <form id="registerForm" onSubmit={handleSubmit} className={styles.registerForm}>
          <div className={styles.formField}>
            <input type="text" id="contactName" name="contactName" className={styles.formInput} required placeholder="Full Name" value={formData.contactName} onChange={handleChange} />
            <label htmlFor="contactName" className={styles.formLabel}>Full Name:</label>
          </div>
          <div className={styles.formField}>
            <input type="email" id="email" name="email" className={styles.formInput} required placeholder="Email" value={formData.email} onChange={handleChange} />
            <label htmlFor="email" className={styles.formLabel}>Email:</label>
          </div>
          <div className={`${styles.formField} ${styles.positionRelative}`}>
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              className={styles.formInput}
              required
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
            />
            <label htmlFor="password" className={styles.formLabel}>Password:</label>
            <button type="button" className={styles.togglePassword} onClick={togglePassword} tabIndex={-1}>
              <i className={`fas fa-${showPassword ? 'eye' : 'eye-slash'}`}></i>
            </button>
          </div>
          <div className={`${styles.formField} ${styles.positionRelative}`}>
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="confirmPassword"
              name="confirmPassword"
              className={styles.formInput}
              required
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleChange}
            />
            <label htmlFor="confirmPassword" className={styles.formLabel}>Confirm Password:</label>
            <button type="button" className={styles.togglePassword} onClick={toggleConfirmPassword} tabIndex={-1}>
              <i className={`fas fa-${showConfirmPassword ? 'eye' : 'eye-slash'}`}></i>
            </button>
          </div>
          
          {/* School Code - Required */}
          <div className={styles.formField}>
            <input 
              type="text" 
              id="schoolCode" 
              name="schoolCode" 
              className={styles.formInput} 
              required 
              placeholder="School Code" 
              value={formData.schoolCode} 
              onChange={handleSchoolCodeChange}
            />
            <label htmlFor="schoolCode" className={styles.formLabel}>School Code: *</label>
            <div className={styles.helpText}>Enter your school's unique code</div>
            {loadingSchool && <div className={styles.helpText}>Loading school details...</div>}
          </div>

          {/* School Information Display */}
          {schoolInfo && (
            <div className="alert alert-info">
              <i className="fas fa-check-circle"></i>
              <strong>School Found:</strong> {schoolInfo.schoolName}
              {schoolInfo.isExisting && (
                <div className="small text-muted mt-1">
                  This school is already registered in our system.
                </div>
              )}
            </div>
          )}

          {/* Human Check */}
          <div className={styles.formField}>
            <HumanCheck 
              onValidationChange={setHumanCheckValid}
              error={error && !humanCheckValid ? 'Please answer the question correctly to proceed.' : ''}
              formType="registerTeacher"
            />
          </div>

          {/* Address Fields - Only shown when school code is entered */}
          {showAddressFields && (
            <>
              <div className={styles.formField}>
                <input
                  type="text"
                  id="schoolName"
                  name="schoolName"
                  className={styles.formInput}
                  placeholder="School Name"
                  value={formData.schoolName}
                  onChange={handleChange}
                  autoComplete="off"
                />
                <label htmlFor="schoolName" className={styles.formLabel}>School Name:</label>
              </div>
              <div className={styles.formField}>
                <textarea
                  id="address"
                  name="address"
                  className={styles.formInput}
                  placeholder="School Address"
                  rows="3"
                  value={formData.address}
                  onChange={handleChange}
                  autoComplete="off"
                />
                <label htmlFor="address" className={styles.formLabel}>School Address:</label>
              </div>
              <div className={styles.formField}>
                <input 
                  type="text" 
                  id="postZipCode" 
                  name="postZipCode" 
                  className={styles.formInput} 
                  placeholder="Postcode/Zipcode" 
                  value={formData.postZipCode} 
                  onChange={handleChange} 
                  autoComplete="off"
                />
                <label htmlFor="postZipCode" className={styles.formLabel}>Postcode/Zipcode:</label>
              </div>
              <div className={`${styles.formField} ${styles.positionRelative}`}>
                <input
                  type="text"
                  id="website"
                  name="website"
                  className={styles.formInput}
                  placeholder="School Website"
                  value={formData.website}
                  onChange={handleChange}
                  style={{ paddingRight: '2.5rem' }}
                />
                <label htmlFor="website" className={styles.formLabel}>School Website:</label>
                {formData.website && (
                  <a
                    href={formData.website.startsWith('http') ? formData.website : `https://${formData.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.websiteLink}
                    tabIndex={-1}
                    aria-label="Open website in new tab"
                  >
                    <i className="fas fa-link"></i>
                  </a>
                )}
              </div>
            </>
          )}

          <button type="submit" className={styles.submitButton} disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Teacher Account'}
          </button>
        </form>

        <div className={styles.registerFooter}>
          <span>Already have an account? <Link to="/login">Login</Link></span>
          {/* Student registration link – hidden for now; remove className to show */}
          <p className={styles.studentRegistrationHidden}>
            Want to register as a student?{' '}
            <a href="/register/student">Student registration</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterTeacher; 